import Flashbar, { type FlashbarProps } from '@cloudscape-design/components/flashbar';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type Notification = Omit<FlashbarProps.MessageDefinition, 'id'> & { id?: string };

interface NotificationContextValue {
  notify: (n: Notification) => string;
  dismiss: (id: string) => void;
  items: ReadonlyArray<FlashbarProps.MessageDefinition>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

let nextId = 1;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((cur) => cur.filter((n) => n.id !== id));
  }, []);

  const notify = useCallback(
    (n: Notification): string => {
      const id = n.id ?? `n-${nextId++}`;
      setItems((cur) => [
        ...cur,
        {
          ...n,
          id,
          dismissible: n.dismissible ?? true,
          onDismiss: () => dismiss(id),
        },
      ]);
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ notify, dismiss, items }), [notify, dismiss, items]);
  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used inside <NotificationProvider>');
  return ctx;
}

export function GlobalFlashbar() {
  const { items } = useNotifications();
  return <Flashbar items={[...items]} stackItems />;
}
